import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ViewListIcon from '@mui/icons-material/ViewList';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {
    TextField, Select, MenuItem, Button, Box, Paper, Fade, Collapse, IconButton, Tooltip,
    FormControl, InputLabel, Typography, Menu
} from '@mui/material';

import { updateFilters, getCurrentMonthValue, getMonthKey } from '../../redux/monthlyBalanceSlice';
import { loadAccount } from '../../redux/financeAccountSlice';
import { MONTH_NAMES } from './monthNames';

const FilterContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
        width: '100%',
    },
}));

const FilterHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'center',
    flexWrap: 'wrap',
    [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
    },
}));

const MonthNavigator = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    backgroundColor: 'white',
    border: '2px solid #e0e0e0',
    borderRadius: '12px',
    padding: theme.spacing(0.5, 1),
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
}));

const ViewModeSelect = styled(FormControl)(({ theme }) => ({
    minWidth: '200px',
    '& .MuiInputBase-root': {
        height: '48px',
        borderRadius: '12px',
        backgroundColor: 'white',
    },
    '& .MuiInputLabel-root': {
        fontWeight: 600,
    },
    [theme.breakpoints.down('sm')]: {
        minWidth: 'unset',
        width: '100%',
    },
}));

const MonthLabel = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '1rem',
    color: 'rgb(0,98,105)',
    minWidth: '170px',
    textAlign: 'center',
    textTransform: 'capitalize',
}));

const ToggleButton = styled(Button)(({ theme }) => ({
    borderRadius: '12px',
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: 'none',
    backgroundColor: 'rgb(0,98,105)',
    color: 'white',
    boxShadow: '0 4px 16px rgba(0, 98, 105, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: 'rgb(0,85,91)',
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 98, 105, 0.4)',
    },
    [theme.breakpoints.down('sm')]: {
        flex: 1,
    },
}));

const QuickActionButton = styled(IconButton)(({ theme }) => ({
    backgroundColor: 'white',
    color: theme.palette.text.primary,
    border: '2px solid #e0e0e0',
    width: '48px',
    height: '48px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: '#f5f5f5',
        borderColor: 'rgb(0,98,105)',
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    },
}));

const CollapsibleWrapper = styled(Box)(({ theme }) => ({
    position: 'absolute',
    left: 0,
    right: 0,
    top: '100%',
    zIndex: 10,
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        position: 'relative',
        top: 'auto',
        marginTop: theme.spacing(2),
    },
}));

const FilterPanel = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: '16px',
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(0, 0, 0, 0.06)',
    marginTop: theme.spacing(2),
}));

const StyledForm = styled('form')(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    alignItems: 'flex-end',
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
    }
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: '180px',
    flex: 1,
    [theme.breakpoints.down('sm')]: {
        minWidth: 'unset',
        width: '100%',
    }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: theme.palette.background.paper,
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '2px',
        },
    },
    '& .MuiInputLabel-root': {
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },
    },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
    borderRadius: '12px',
    backgroundColor: theme.palette.background.paper,
    '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    borderRadius: '12px',
    padding: theme.spacing(1.5, 3),
    fontWeight: 600,
    textTransform: 'none',
    minHeight: '48px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
    },
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        marginTop: theme.spacing(1),
    }
}));

const PrimaryButton = styled(ActionButton)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '&:hover': {
        backgroundColor: theme.palette.primary.dark,
    },
}));

const SecondaryButton = styled(ActionButton)(({ theme }) => ({
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    '&:hover': {
        backgroundColor: theme.palette.grey[200],
    },
}));

const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        width: '100%',
    }
}));

/**
 * Moves a 'YYYY-MM' month value by the given number of months.
 */
export function addMonthsToMonthValue(monthValue, numberOfMonths) {
    const [year, month] = monthValue.split('-').map(value => parseInt(value, 10));
    const date = new Date(year, month - 1 + numberOfMonths, 1);
    return getCurrentMonthValue(date);
}

export function formatMonthLabel(monthValue) {
    if (!monthValue) return '';
    const [year, month] = monthValue.split('-');
    const monthName = MONTH_NAMES[parseInt(month, 10) - 1] || getMonthKey(monthValue);
    return `${monthName} de ${year}`;
}

function submitSearch(event, searchFilters, updateFilters, setIsFilterOpen) {
    event.preventDefault();
    updateFilters(searchFilters);
    setIsFilterOpen(false);
};

/**
 * When responsibles are selected, only their accounts should be considered.
 */
function updateUserFilter(searchFilters, users, accounts) {
    const financeAccounts = (accounts || [])
        .filter(account => account.user && users.includes(account.user.id))
        .map(account => account.id);

    return { ...searchFilters, users, financeAccounts };
}

function resetFilters(month, updateSearchFilters, updateFilters, setIsFilterOpen) {
    const searchFilters = {
        month,
        users: [],
        financeAccounts: []
    };
    updateSearchFilters(searchFilters);
    updateFilters(searchFilters);
    setIsFilterOpen(false);
}

function Filter(props) {
    const dispatch = useDispatch();
    const filterFields = useSelector(state => state.monthlyBalance.filterFields);
    const accounts = useSelector(state => state.financeAccount.items);
    const users = useSelector(state => state.account.users);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);
    const onExport = props.onExport;
    const onExportPdf = props.onExportPdf;
    const viewMode = props.viewMode || 'grouped';
    const onViewModeChange = props.onViewModeChange;

    const updateFiltersAction = (filters) => dispatch(updateFilters(filters));
    const [searchFilters, updateSearchFilters] = useState({ ...filterFields });

    const loadAccounts = useCallback(() => dispatch(loadAccount()), [dispatch]);

    // Keep the local form in sync when the month changes outside of the form
    useEffect(() => {
        updateSearchFilters(filters => ({ ...filters, month: filterFields.month }));
    }, [filterFields.month]);

    useEffect(() => {
        if ((accounts || []).length === 0) {
            loadAccounts();
        }
    }, [accounts, loadAccounts]);

    const goToMonth = (numberOfMonths) => {
        const month = addMonthsToMonthValue(searchFilters.month, numberOfMonths);
        const updatedFilters = { ...searchFilters, month };
        updateSearchFilters(updatedFilters);
        updateFiltersAction(updatedFilters);
        setIsFilterOpen(false);
    };

    const filterForm = (
        <Fade in={true} timeout={300}>
            <FilterPanel elevation={0}>
                <StyledForm onSubmit={event => submitSearch(event, searchFilters, updateFiltersAction, setIsFilterOpen)}>
                    <StyledFormControl>
                        <StyledTextField
                            id="month"
                            name="month"
                            label="Mês"
                            type="month"
                            variant="outlined"
                            autoFocus
                            value={searchFilters.month}
                            onChange={event => updateSearchFilters({ ...searchFilters, month: event.target.value })}
                            InputLabelProps={{ shrink: true }}
                        />
                    </StyledFormControl>
                    <StyledFormControl>
                        <InputLabel id="users-label">Responsável</InputLabel>
                        <StyledSelect
                            labelId="users-label"
                            id="users"
                            multiple
                            value={searchFilters.users}
                            label="Responsável"
                            onChange={event => updateSearchFilters(updateUserFilter(searchFilters, event.target.value, accounts))}
                        >
                            {(users || []).map(user => <MenuItem key={user.id} value={user.id}>{`${user.name}`}</MenuItem>)}
                        </StyledSelect>
                    </StyledFormControl>
                    <StyledFormControl>
                        <InputLabel id="accounts-label">Conta</InputLabel>
                        <StyledSelect
                            labelId="accounts-label"
                            id="financeAccounts"
                            multiple
                            value={searchFilters.financeAccounts}
                            label="Conta"
                            onChange={event => updateSearchFilters({ ...searchFilters, financeAccounts: event.target.value })}
                        >
                            {(accounts || []).map(account => <MenuItem key={account.id} value={account.id}>{`${account.name}`}</MenuItem>)}
                        </StyledSelect>
                    </StyledFormControl>

                    <ButtonGroup>
                        <PrimaryButton
                            type="submit"
                            variant="contained"
                            startIcon={<FilterListIcon />}
                        >
                            Filtrar
                        </PrimaryButton>
                        <SecondaryButton
                            type="button"
                            variant="outlined"
                            startIcon={<ClearIcon />}
                            onClick={() => resetFilters(getCurrentMonthValue(), updateSearchFilters, updateFiltersAction, setIsFilterOpen)}
                        >
                            Limpar
                        </SecondaryButton>
                    </ButtonGroup>
                </StyledForm>
            </FilterPanel>
        </Fade>
    );

    return (
        <FilterContainer>
            <FilterHeader>
                <MonthNavigator>
                    <Tooltip title="Mês anterior" placement="top">
                        <IconButton onClick={() => goToMonth(-1)} size="small">
                            <ChevronLeftIcon />
                        </IconButton>
                    </Tooltip>
                    <MonthLabel>{formatMonthLabel(searchFilters.month)}</MonthLabel>
                    <Tooltip title="Próximo mês" placement="top">
                        <IconButton onClick={() => goToMonth(1)} size="small">
                            <ChevronRightIcon />
                        </IconButton>
                    </Tooltip>
                </MonthNavigator>
                <ViewModeSelect>
                    <InputLabel id="view-mode-label">Visão</InputLabel>
                    <Select
                        labelId="view-mode-label"
                        id="view-mode"
                        value={viewMode}
                        label="Visão"
                        onChange={(event) => onViewModeChange && onViewModeChange(event.target.value)}
                        renderValue={(selected) => (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                {selected === 'transactions' ? <ViewListIcon fontSize="small" /> : <AccountTreeIcon fontSize="small" />}
                                {selected === 'transactions' ? 'Transações' : 'Agrupado'}
                            </Box>
                        )}
                    >
                        <MenuItem value="grouped" sx={{ gap: 1.5 }}>
                            <AccountTreeIcon fontSize="small" />
                            Agrupado
                        </MenuItem>
                        <MenuItem value="transactions" sx={{ gap: 1.5 }}>
                            <ViewListIcon fontSize="small" />
                            Transações
                        </MenuItem>
                    </Select>
                </ViewModeSelect>
                <ToggleButton
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    startIcon={<FilterListIcon />}
                    endIcon={isFilterOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                    {isFilterOpen ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </ToggleButton>
                <Tooltip title="Exportar" placement="top">
                    <QuickActionButton onClick={(event) => setExportMenuAnchor(event.currentTarget)}>
                        <FileDownloadIcon />
                    </QuickActionButton>
                </Tooltip>
                <Menu
                    anchorEl={exportMenuAnchor}
                    open={Boolean(exportMenuAnchor)}
                    onClose={() => setExportMenuAnchor(null)}
                >
                    <MenuItem onClick={() => { setExportMenuAnchor(null); onExport(); }}>
                        <FileDownloadIcon fontSize="small" sx={{ mr: 1.5 }} />
                        Exportar CSV
                    </MenuItem>
                    <MenuItem onClick={() => { setExportMenuAnchor(null); onExportPdf && onExportPdf(); }}>
                        <PictureAsPdfIcon fontSize="small" sx={{ mr: 1.5 }} />
                        Exportar PDF
                    </MenuItem>
                </Menu>
            </FilterHeader>

            <CollapsibleWrapper>
                <Collapse in={isFilterOpen} timeout={300}>
                    {filterForm}
                </Collapse>
            </CollapsibleWrapper>
        </FilterContainer>
    );
}

export default Filter;
